import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Selects a specific slide by index.
   * @param {number} index - The slide index to show.
   */
  select(index) {
    this.current = index;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    const tick = () => {
      if (!this.matches(':hover') && !document.hidden) {
        this.next();
      }
      this.#interval = setTimeout(tick, interval);
    };
    this.#interval = setTimeout(tick, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearTimeout(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.toggleAttribute('data-reverse', current < this.#current);
    this.#current = current;

    const slides = this.refs.slides ?? [];
    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    const activeSlide = slides[relativeIndex];
    const needsReset = activeSlide?.hasAttribute('data-leaving');

    if (needsReset) {
      // Reset position without animation
      activeSlide.style.transition = 'none';
      activeSlide.removeAttribute('data-leaving');

      // Double rAF: first lets the browser paint the reset position,
      // second re-enables animation so the slide enters from the correct direction
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          activeSlide.style.transition = '';
          this.#applySlideStates(slides, relativeIndex);
        });
      });
    } else {
      this.#applySlideStates(slides, relativeIndex);
    }

    this.#syncSectionColor(activeSlide);
  }

  /**
   * Sets aria-hidden and data-leaving on all slides.
   * @param {HTMLElement[]} slides
   * @param {number} activeIndex
   */
  #applySlideStates(slides, activeIndex) {
    for (const [index, slide] of slides.entries()) {
      if (index === activeIndex) {
        slide.setAttribute('aria-hidden', 'false');
      } else {
        const wasVisible = slide.getAttribute('aria-hidden') === 'false';
        if (wasVisible) {
          slide.setAttribute('data-leaving', '');
        }
        slide.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Copies the active slide's color class to the section element
   * so the section background animates via CSS transition.
   * @param {HTMLElement} [activeSlide]
   */
  #syncSectionColor(activeSlide) {
    if (!activeSlide) return;

    const section = this.closest('.announcement-bar');
    if (!section) return;

    const colorClass = [...activeSlide.classList].find((cls) => cls.startsWith('color-'));
    if (!colorClass) return;

    for (const cls of [...section.classList]) {
      if (cls.startsWith('color-')) {
        section.classList.remove(cls);
      }
    }
    section.classList.add(colorClass);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
